// Queries for the generic gatherings discovery/detail UI.

export const MY_CONNECTIONS_QUERY = /* GraphQL */ `
  query MyConns {
    myConnections {
      id
      name
      linkedinUrl
      pictureUrl
      metAtGatheringTitle
      metAt
    }
  }
`;

export const GATHERINGS_QUERY = /* GraphQL */ `
  query Gatherings {
    gatherings {
      id title description
      kind
      location
      startsAt
      endsAt
      capacity
      coverUrl
      visibility
      status
      circleId
      circle { id name }
      host { id name firstName lastName pictureUrl }
      participantCount
      currentUserParticipant { id status joinedAt }
    }
  }
`;

export const CIRCLE_GATHERINGS_QUERY = /* GraphQL */ `
  query CircleGatherings($circleId: ID!) {
    circleGatherings(circleId: $circleId) {
      id title description
      kind
      location
      startsAt
      endsAt
      capacity
      coverUrl
      visibility
      participantCount
      currentUserParticipant { id status joinedAt }
    }
  }
`;

export const GATHERING_QUERY = /* GraphQL */ `
  query Gathering($id: ID!) {
    gathering(id: $id) {
      id title description
      kind
      location locationLat locationLng onlineUrl
      startsAt
      endsAt
      capacity
      coverUrl
      visibility
      circleId
      circle { id name }
      participantCount
      currentUserParticipant { id status joinedAt }
    }
    me { id email }
  }
`;

export const CREATE_GATHERING_MUTATION = /* GraphQL */ `
  mutation CreateGathering(
    $title: String!, $description: String, $kind: GatheringKind!,
    $location: String, $locationLat: Float, $locationLng: Float,
    $onlineUrl: String, $startsAt: DateTime!, $endsAt: DateTime!,
    $capacity: Int, $coverUrl: String, $visibility: String!, $circleId: ID
  ) {
    createGathering(
      input: {
        title: $title, description: $description, kind: $kind,
        location: $location, locationLat: $locationLat, locationLng: $locationLng,
        onlineUrl: $onlineUrl, startsAt: $startsAt, endsAt: $endsAt,
        capacity: $capacity, coverUrl: $coverUrl, visibility: $visibility, circleId: $circleId,
        isPublished: true
      }
    ) {
      id title startsAt endsAt
    }
  }
`;

export const SUGGESTED_GATHERINGS_QUERY = /* GraphQL */ `
  query SuggestedGatherings {
    suggestedGatherings {
      id title description kind location coverUrl
      circleId circleName
      host { name pictureUrl }
      slots { id label isSoldOut }
    }
  }
`;

const GATHERING_SERIES_FIELDS = /* GraphQL */ `
  id
  name
  tagline
  description
  imageUrl
  interestCount
  hasInterest
  isPublished
  audience
  nextGatheringStatus
  audienceSize
  nextGathering {
    id
    kind
    startsAt
    endsAt
    capacity
    externalLink
    participantCount
    isSignedUp
  }
  upcomingSessions {
    id
    kind
    startsAt
    endsAt
    capacity
    externalLink
    participantCount
    isSignedUp
  }
  circles { id name }
  gatheringType {
    id
    slug
    name
    description
    kind
    capacityMin
    capacityMax
    durationMinutes
    isModerated
  }
  venue {
    id
    name
    formattedAddress
    lat
    lng
    photoUrl
    googleMapsUrl
    cuisine
  }
`;

export const MY_GATHERING_SERIES_QUERY = /* GraphQL */ `
  query MyGatheringSeries {
    myGatheringSeries { ${GATHERING_SERIES_FIELDS} }
  }
`;

export const GATHERING_SERIES_CARD_QUERY = /* GraphQL */ `
  query GatheringSeriesCard($id: ID!) {
    gatheringSeriesCard(id: $id) { ${GATHERING_SERIES_FIELDS} }
  }
`;

export const REGISTER_SERIES_INTEREST_MUTATION = /* GraphQL */ `
  mutation RegisterSeriesInterest($seriesId: ID!) {
    registerSeriesInterest(seriesId: $seriesId) {
      id interestCount hasInterest
    }
  }
`;

export const REMOVE_SERIES_INTEREST_MUTATION = /* GraphQL */ `
  mutation RemoveSeriesInterest($seriesId: ID!) {
    removeSeriesInterest(seriesId: $seriesId) {
      id interestCount hasInterest
    }
  }
`;

export const DISMISS_GATHERING_SERIES_MUTATION = /* GraphQL */ `
  mutation DismissGatheringSeries($seriesId: ID!) {
    dismissGatheringSeries(seriesId: $seriesId)
  }
`;

// The RSVP mutations are canonical (reserveGathering / cancelReservation).

export const GATHERING_DETAIL_RESERVE_MUTATION = /* GraphQL */ `
  mutation ReserveGathering($gatheringId: ID!, $email: String) {
    reserveGathering(gatheringId: $gatheringId, email: $email) {
      id
      participantCount
      currentUserParticipant { id status joinedAt }
    }
  }
`;

// Admin: gathering series CRUD.

export const ADMIN_GATHERING_TYPES_QUERY = /* GraphQL */ `
  query AdminGatheringTypes {
    adminGatheringTypes { id slug name kind capacityMin capacityMax durationMinutes isModerated }
  }
`;

export const ADMIN_GATHERING_SERIES_QUERY = /* GraphQL */ `
  query AdminGatheringSeries {
    adminGatheringSeries {
      id name tagline description imageUrl
      isPublished audience
      audienceSize
      circles { id name }
      gatheringType { id slug name kind capacityMin capacityMax durationMinutes }
      nextGathering { id startsAt endsAt }
      instances { id startsAt endsAt capacity participantCount externalLink isPublished }
    }
  }
`;

export const ADMIN_GATHERING_SERIES_BY_ID_QUERY = /* GraphQL */ `
  query AdminGatheringSeriesById($id: ID!) {
    adminGatheringSeriesById(id: $id) {
      id name tagline description imageUrl
      isPublished audience
      audienceSize
      circles { id name }
      gatheringType { id slug name kind capacityMin capacityMax durationMinutes }
      instances {
        id title kind startsAt endsAt capacity participantCount
        location onlineUrl externalLink isPublished
      }
    }
  }
`;

export const CREATE_GATHERING_SERIES_MUTATION = /* GraphQL */ `
  mutation CreateGatheringSeries($input: CreateGatheringSeriesInput!) {
    createGatheringSeries(input: $input) { id }
  }
`;

export const UPDATE_GATHERING_SERIES_MUTATION = /* GraphQL */ `
  mutation UpdateGatheringSeries($id: ID!, $input: UpdateGatheringSeriesInput!) {
    updateGatheringSeries(id: $id, input: $input) { id }
  }
`;

export const DELETE_GATHERING_SERIES_MUTATION = /* GraphQL */ `
  mutation DeleteGatheringSeries($id: ID!) { deleteGatheringSeries(id: $id) }
`;

export const UPLOAD_SERIES_IMAGE_MUTATION = /* GraphQL */ `
  mutation UploadSeriesImage($contentType: String!, $dataBase64: String!) {
    uploadSeriesImage(contentType: $contentType, dataBase64: $dataBase64)
  }
`;

export const AUDIENCE_ESTIMATE_QUERY = /* GraphQL */ `
  query AudienceEstimate($audience: SeriesAudience!, $circleIds: [ID!]!) {
    audienceEstimate(audience: $audience, circleIds: $circleIds)
  }
`;

export const GATHERING_DETAIL_CANCEL_MUTATION = /* GraphQL */ `
  mutation CancelReservation($gatheringId: ID!) {
    cancelReservation(gatheringId: $gatheringId) {
      id
      participantCount
      currentUserParticipant { id status }
    }
  }
`;
